import { create } from 'zustand';

import { getWalletBalance } from 'Api/walletRechargeAPI';
import { getPartnerCurrency } from '../hooks/usePartnerCurrency';
import useAuthStore from './useAuthStore';

/**
 * The partner's INR wallet, shared by the header chip and the Wallet page.
 *
 * ── Why a store and not local state in each ────────────────────────────────
 * The balance is now shown in the header on EVERY screen, and again on the
 * wallet page. Two copies of the same figure fetched independently is how you
 * get a header saying ₹2,500 next to a page saying ₹5,500 after a top-up —
 * the exact inconsistency a balance must never show. One record, one loader,
 * both read it.
 *
 * ── ⚠️ THE WALLET IS NOT ALWAYS INR ───────────────────────────────────────
 * Most partners do not trade in rupees — today's data has USD, QAR and INR
 * wallets, and more partners hold NO INR wallet than hold one. Loading a fixed
 * 'INR' here would show a USD partner ₹0 while their real balance sat
 * invisible, which is worse than showing nothing.
 *
 * So the currency comes from the SESSION, via the same resolver
 * CurrencyDisplay uses (login response -> branches[] -> currency). One currency
 * per session is the right model: a partner portal is single-tenant by
 * definition. No amount is ever converted, here or anywhere.
 *
 * ⚠️ Razorpay settles INR only, so TOPPING UP is INR-only even though SHOWING
 * a balance is not. `canTopUp` below is what the UI gates the add control on —
 * offering it to a USD partner would create an INR wallet they cannot spend
 * from. The engine refuses any other currency regardless.
 *
 * The currency-scoped balance call is used deliberately: the unscoped one
 * returns an array and omits lifetime totals and lastTransactionAt, which read
 * as "never credited" rather than "not asked for".
 *
 * ── ⚠️ THE WALLET IS STAMPED WITH THE PARTNER IT BELONGS TO ────────────────
 * This store is module-level, so it OUTLIVES a logout. Without the stamp,
 * signing out and back in as a different partner left the previous partner's
 * balance on screen — and since the currency label comes from the new session,
 * it was relabelled too: partner A's ₹7,440 was shown to partner B as
 * "USD 7,440". One tenant's financial position displayed to another.
 *
 * `reset()` on logout is the first line of defence and the selectors below are
 * the second: they return nothing when the stamp does not match the session
 * asking, so a missed reset cannot leak a balance across partners. A failed
 * load CLEARS the wallet for the same reason — a stale figure under a new
 * label is worse than no figure at all.
 */

/** The only currency Razorpay can fund. */
export const TOPUP_CURRENCY = 'INR';

const useWalletStore = create((set, get) => ({
  wallet: null,
  loading: false,
  error: null,
  /** False until the first attempt settles, so the chip can stay silent. */
  loaded: false,
  /** The session's trading currency — what the balance above is denominated in. */
  currency: null,
  /** Which partner `wallet` belongs to. Never render it for anybody else. */
  partnerCode: null,
  /** Whether this partner can be topped up at all. INR wallets only. */
  canTopUp: false,

  /**
   * Fetch the wallet.
   *
   * Single-flight: the header mounts on every route change and the wallet page
   * loads on its own, so two callers asking at once is the normal case rather
   * than an edge one. The in-flight promise is held on the store so both await
   * the same request instead of racing two.
   */
  load: async () => {
    const { _inFlight } = get();
    if (_inFlight) return _inFlight;

    const promise = (async () => {
      const currency = getPartnerCurrency();
      const partnerCode = useAuthStore.getState().partner?.partnerCode ?? null;
      set({
        loading: true,
        currency,
        partnerCode,
        canTopUp: currency === TOPUP_CURRENCY,
        // Anything held from a previous partner goes NOW, not when the reply
        // lands — the label has already flipped to the new session's currency.
        wallet: get().partnerCode === partnerCode ? get().wallet : null,
      });

      const result = await getWalletBalance(currency);

      // The session may have changed under a slow request. Dropping the reply
      // is right: it describes a partner who is no longer signed in.
      if (useAuthStore.getState().partner?.partnerCode !== partnerCode) {
        set({ _inFlight: null });
        return get().wallet;
      }

      if (result.ok) {
        set({ wallet: result.data, error: null });
      } else {
        // No wallet yet is ORDINARY — a partner has no row until their first
        // top-up creates one, and a 404 here says exactly that. The wallet is
        // cleared rather than left stale: a previous partner's figure under
        // this partner's currency label is the one outcome worth preventing.
        set({ wallet: null, error: result.message });
      }
      set({ loading: false, loaded: true, _inFlight: null });
      return get().wallet;
    })();

    set({ _inFlight: promise });
    return promise;
  },

  /** Called after a confirmed credit. Re-reads rather than adding locally. */
  refresh: () => {
    set({ _inFlight: null });
    return get().load();
  },

  /**
   * Re-read the balance after a booking was paid from the wallet — SMB-4171.
   *
   * Nothing used to do this. `refresh` was wired to top-ups and to sign-in only,
   * so a wallet-paid booking left the header showing the PRE-booking figure
   * until something unrelated happened to reload it. A partner reading an
   * available balance that is too high is the one wrong number here that leads
   * to more bookings being taken than the wallet can cover.
   *
   * ── Why it re-checks rather than reading once ─────────────────────────────
   * The debit is not ours. The booking-complete signal comes from the embedded
   * traveller app the moment ITS payment settles, while the ledger entry is
   * posted by the partner engine — two services, no ordering guarantee between
   * them. A single read fired on the signal can therefore land before the debit
   * does and cache the old number, which looks exactly like the bug it fixes.
   *
   * So: read immediately, and if the balance has not moved, look again a few
   * times over a short window. It stops the moment the figure changes, so the
   * common case costs one request. It does NOT subtract the booking amount
   * locally — a balance is the ledger's to state, and a guess that disagrees
   * with the next honest read is worse than being a second late.
   *
   * If the engine never posts the debit at all, this settles on the engine's
   * answer rather than inventing one — the balance stays wrong, and it is wrong
   * in the place that is actually broken.
   */
  refreshAfterBooking: async () => {
    /** ~9s total. Long enough for a ledger write, short enough to not hang about. */
    const RETRY_DELAYS_MS = [1500, 3000, 4500];
    const balanceOf = (w) => w?.balances?.balance ?? null;

    const before = balanceOf(get().wallet);
    const partnerCode = useAuthStore.getState().partner?.partnerCode ?? null;

    let wallet = await get().refresh();
    if (balanceOf(wallet) !== before) return wallet;

    for (const delay of RETRY_DELAYS_MS) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, delay); });
      // Signing out, or switching partner, mid-wait. `load` drops a reply for
      // the wrong partner already; stopping here also drops the retries.
      if (useAuthStore.getState().partner?.partnerCode !== partnerCode) return get().wallet;
      // eslint-disable-next-line no-await-in-loop
      wallet = await get().refresh();
      if (balanceOf(wallet) !== before) return wallet;
    }
    return wallet;
  },

  reset: () =>
    set({
      wallet: null,
      loading: false,
      error: null,
      loaded: false,
      currency: null,
      partnerCode: null,
      canTopUp: false,
      _inFlight: null,
    }),

  _inFlight: null,
}));

export default useWalletStore;

/**
 * The wallet, but only for the partner currently signed in.
 *
 * The second line of defence described at the top of this file. Components read
 * through this rather than `s.wallet` so a stamp from another partner can never
 * reach a screen, whatever happened to `reset()`.
 */
export const useOwnWallet = () => {
  const wallet = useWalletStore((s) => s.wallet);
  const stamped = useWalletStore((s) => s.partnerCode);
  const current = useAuthStore((s) => s.partner?.partnerCode);
  return stamped != null && String(stamped) === String(current) ? wallet : null;
};
